import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Row, Col, Form, Button, Spinner, Table, Alert } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import {
    FaSearch, FaArrowUp, FaArrowDown, FaChartLine,
    FaFilter, FaTimes, FaCalendarAlt, FaWallet,
    FaMoneyBillWave, FaShoppingCart, FaClipboardList,
    FaTruck, FaLightbulb, FaUserTie, FaEyeSlash
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const TYPE_CONFIG = {
    sale: { label: "Sale", icon: FaShoppingCart, badgeClass: "bg-success" },
    appointment: { label: "Appointment", icon: FaClipboardList, badgeClass: "bg-info" },
    vendor: { label: "Vendor", icon: FaTruck, badgeClass: "bg-warning" },
    daily_expense: { label: "Daily Expense", icon: FaLightbulb, badgeClass: "bg-danger" },
    utility_expense: { label: "Utility Expense", icon: FaLightbulb, badgeClass: "bg-danger" },
    salary_expense: { label: "Salary Expense", icon: FaUserTie, badgeClass: "bg-danger" },
    expense: { label: "Expense", icon: FaMoneyBillWave, badgeClass: "bg-danger" }
};

const formatDateForAPI = (date) => {
    if (!date) return null;
    return date.toISOString().split('T')[0];
};

const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0.00';
    return amount;
};

const formatDisplayDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

export default function FinanceReport() {
    const { t } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState({ total_in: 0, total_out: 0, balance: 0 });
    const [selectedMonthDate, setSelectedMonthDate] = useState(new Date());
    const [reportPeriod, setReportPeriod] = useState({ start_date: '', end_date: '' });
    const [showFilters, setShowFilters] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    
    // Access code state & Alert visibility state
    const [accessCode, setAccessCode] = useState("");
    const [showAlert, setShowAlert] = useState(false);

    useEffect(() => {
        const today = new Date();
        handleFetchForMonth(today);
    }, []);

    // Trigger success alert when the correct secret code is typed in
    useEffect(() => {
        if (accessCode === "JPHLHR1122") {
            setShowAlert(true);
            // Optional: Auto-hide the success toast alert after 3 seconds
            const timer = setTimeout(() => setShowAlert(false), 3000);
            return () => clearTimeout(timer);
        } else {
            setShowAlert(false);
        }
    }, [accessCode]);

    const handleFetchForMonth = (targetMonthDate) => {
        if (!targetMonthDate) return;

        let dateToUse = targetMonthDate;
        if (typeof targetMonthDate === 'string') {
            dateToUse = new Date(targetMonthDate);
        }

        const year = dateToUse.getFullYear();
        const month = dateToUse.getMonth();

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        lastDay.setHours(23, 59, 59, 999);

        setSelectedMonthDate(dateToUse);
        fetchReport(firstDay, lastDay);
    };

    const fetchReport = async (start, end) => {
        setLoading(true);
        try {
            const params = {};
            if (start) params.start_date = formatDateForAPI(start);
            if (end) params.end_date = formatDateForAPI(end);

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/finance-report`,
                {
                    params,
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data?.status) {
                setReportData(response.data.data || []);
                setSummary(response.data.summary || { total_in: 0, total_out: 0, balance: 0 });
                setReportPeriod({
                    start_date: params.start_date || formatDateForAPI(new Date()),
                    end_date: params.end_date || formatDateForAPI(new Date())
                });
            }
        } catch (error) {
            console.error("Error fetching report:", error);
            alert(t('failed_to_load_report'));
        } finally {
            setLoading(false);
        }
    };

    const handleHideData = () => {
        setAccessCode(""); // Resetting the code automatically hides summaries and components
    };

    const totalIncomeFiltered = reportData
        .filter(t => t.transaction_type === 'credit')
        .reduce((sum, i) => sum + (i.amount || 0), 0);
    const totalExpenseFiltered = reportData
        .filter(t => t.transaction_type === 'debit')
        .reduce((sum, e) => sum + (e.amount || 0), 0);
    const netProfit = summary.balance;

    const StatCard = ({ title, value, type, icon: Icon }) => {
        const isPositive = type === 'income' || (type === 'profit' && value >= 0);
        const colorClass = type === 'income' ? 'success' : type === 'expense' ? 'danger' : isPositive ? 'success' : 'danger';

        return (
            <Col xl={4} md={6}>
                <div className={`card h-100 border-0 shadow-sm border-start border-${colorClass} border-4`}>
                    <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="text-muted mb-2 small text-uppercase fw-bold tracking-wider">{title}</h6>
                                <h2 className={`mb-1 text-${colorClass} fw-extrabold`}>
                                    {type === 'profit' && value >= 0 ? '+' : type === 'profit' && value < 0 ? '-' : ''}
                                    {formatCurrency(Math.abs(value))}
                                </h2>
                                <small className="text-muted d-block mt-1">
                                    {t(type === 'income' ? 'All revenue sources' : type === 'expense' ? 'All operational costs' : 'After all expenses')}
                                </small>
                            </div>
                            <div className={`bg-${colorClass} bg-opacity-10 p-3 rounded-3`}>
                                <Icon className={`text-${colorClass}`} size={24} />
                            </div>
                        </div>
                    </div>
                </div>
            </Col>
        );
    };

    const TransactionTable = () => (
        <div className="card">
            <div className="card-header bg-transparent d-flex justify-content-between align-items-center border-bottom pb-3">
                <div>
                    <h5 className="mb-0 fw-semibold">
                        <FaChartLine className="me-2 text-primary" />
                        {t('All Transactions')}
                    </h5>
                    <small className="text-muted">{t('Income and expense transactions')}</small>
                </div>
                <div className="d-flex gap-2">
                    <span className="badge bg-success bg-opacity-10 text-white px-3 py-2">
                        {t('Income')}: {formatCurrency(totalIncomeFiltered)}
                    </span>
                    <span className="badge bg-danger bg-opacity-10 text-white px-3 py-2">
                        {t('Expense')}: {formatCurrency(totalExpenseFiltered)}
                    </span>
                </div>
            </div>
            <div className="table-responsive">
                <Table responsive hover className="mb-0">
                    <thead>
                        <tr>
                            <th className="ps-3">#</th>
                            <th>{t('Type')}</th>
                            <th>{t('Transaction Name')}</th>
                            <th>{t('Date')}</th>
                            <th>{t('Payment Method')}</th>
                            <th className="text-end pe-3">{t('Amount')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center py-5 text-muted">
                                    <FaWallet size={40} className="mb-2 opacity-25" />
                                    <p className="mb-0">{t('No transactions found')}</p>
                                </td>
                            </tr>
                        ) : (
                            reportData.map((item, idx) => {
                                const typeConfig = TYPE_CONFIG[item.type] || TYPE_CONFIG.expense;
                                const Icon = typeConfig.icon;

                                return (
                                    <tr key={idx}>
                                        <td className="ps-3 fw-medium">{idx + 1}</td>
                                        <td>
                                            <span className={`badge ${typeConfig.badgeClass} d-inline-flex align-items-center gap-1 px-2 py-1`}>
                                                <Icon size={14} />
                                                <span>{t(typeConfig.label)}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <div className="fw-medium">{item.name}</div>
                                            <small className="text-muted text-capitalize">
                                                {item.type?.replace(/_/g, ' ')}
                                            </small>
                                        </td>
                                        <td>{formatDisplayDate(item.date)}</td>
                                        <td>
                                            <span className="badge bg-light text-dark border">
                                                {item.payment_method}
                                            </span>
                                        </td>
                                        <td className="text-end pe-3">
                                            <span className={`fw-bold ${item.transaction_type === 'credit' ? 'text-success' : 'text-danger'}`}>
                                                {item.transaction_type === 'credit' ? '+' : '-'} {formatCurrency(item.amount)}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="border-top">
                            <td colSpan="5" className="text-end fw-bold pe-3">{t('Total Balance')}:</td>
                            <td className={`text-end pe-3 fw-bold ${netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                {netProfit >= 0 ? '+' : '-'}{formatCurrency(Math.abs(netProfit))}
                            </td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );

    return (
        <PageLayout>
            <div className="container-fluid py-3">
                
                {/* Floating Success Alert Toast */}
                {showAlert && (
                    <Alert variant="success" className="position-fixed top-0 end-0 m-3 shadow" style={{ zIndex: 1050 }} onClose={() => setShowAlert(false)} dismissible>
                        {t('Access Granted! Financial summary unlocked.')}
                    </Alert>
                )}

                <div className="mb-4">
                    <h2 className="mb-1 fw-bold">
                        <FaChartLine className="me-2 text-primary" />
                        {t('Finance Report')}
                    </h2>
                </div>

                <div className="card mb-4">
                    <div className="card-body">
                        {showFilters && (
                            <div className="row g-3 align-items-end">
                                {/* Month Filter */}
                                <div className="col-md-4">
                                    <Form.Label className="fw-semibold small text-muted mb-1 d-block">
                                        <FaCalendarAlt className="me-1" size={12} />
                                        {t("MONTH")}
                                    </Form.Label>
                                    <input
                                        type="month"
                                        className="form-control"
                                        value={selectedMonth}
                                        onChange={(e) => {
                                            setSelectedMonth(e.target.value);
                                            handleFetchForMonth(e.target.value);
                                        }}
                                    />
                                </div>
                                
                                {/* Access Code Filter Field */}
                                <div className="col-md-5">
                                    <Form.Label className="fw-semibold small text-muted mb-1 d-block">
                                        {t("ACCESS CODE")}
                                    </Form.Label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        placeholder={t("Enter access code")}
                                        value={accessCode}
                                        onChange={(e) => setAccessCode(e.target.value)}
                                    />
                                </div>

                                {/* Dynamic Action Hide Button */}
                                {accessCode === "JPHLHR1122" && (
                                    <div className="col-md-3">
                                        <Button 
                                            variant="secondary" 
                                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                                            onClick={handleHideData}
                                        >
                                            <FaEyeSlash /> {t("Hide Data")}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Secure Data Masking Segment */}
                {accessCode === "JPHLHR1122" ? (
                    <>
                        <Row className="g-3 mb-4">
                            <StatCard
                                title={t('Total Income')}
                                value={summary.total_in}
                                type="income"
                                icon={FaArrowDown}
                            />
                            <StatCard
                                title={t('Total Expense')}
                                value={summary.total_out}
                                type="expense"
                                icon={FaArrowUp}
                            />
                            <StatCard
                                title={t('Net Profit / Loss')}
                                value={summary.balance}
                                type="profit"
                                icon={summary.balance >= 0 ? FaArrowDown : FaArrowUp}
                            />
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t('Loading...')}</p>
                            </div>
                        ) : (
                            <TransactionTable />
                        )}
                    </>
                ) : (
                    <div className="text-center py-5 text-muted border rounded bg-light shadow-sm">
                        <p className="mb-0 fw-medium">
                            {t('Please enter a valid access code to unlock sensitive financial matrices.')}
                        </p>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}